import React, { useEffect, useRef, useState } from 'react';
import { AccountNotificationsModalScreen } from '@/screens/account-notifications-modal-screen';
import { ActionSheetManagerContextProvider } from '@/contexts/action-sheet-manager-context';
import { AddPADScreen } from '@/screens/payments/add-pad-screen';
import { AddPaymentMethodScreen } from '@/screens/payments/add-payment-method-screen';
import { APP_MODAL_ROUTES } from './routes';
import { AppModalStackParams } from './types';
import { AppOverlays } from '@/components/app-overlays';
import { AppTabsContainerStack } from './app-tabs-container-stack';
import { AuthStatus } from '@/enums';
import { BluetoothContextProvider } from '@/contexts/bluetooth-context';
import { ChatsContextProvider } from '@/contexts/chats-context';
import { CheckCaptureScreen } from '@/screens/payments/check-capture-screen';
import { CollectPADConsentScreen } from '@/screens/payments/collect-pad-consent-screen';
import { ContactSelectorScreen } from '@/screens/contact-selector-screen';
import { createStackNavigator } from '@react-navigation/stack';
import { CreateSubscriptionScreen } from '@/screens/create-subscription-screen';
import { CreditRequestStack } from './credit-request-stack';
import { DSRToolingStack } from './dsr-tooling-stack';
import { EditNoteScreen } from '@/screens/partners/edit-note-screen';
import { EditOrderItemNoteModalScreen } from '@/screens/partners/edit-order-item-note-modal-screen';
import { EditSubscriptionScreen } from '@/screens/edit-subscription-screen';
import {
  flexModalCardStyle,
  useModalStackScreenOptions,
} from '@/hooks/use-modal-stack-screen-options';
import { HiddenAlertsStack } from './hidden-alerts-stack';
import { ImpressionContextProvider } from '@/contexts/impression-context';
import { InvoiceDetailModalScreen } from '@/screens/invoice-detail/invoice-detail-modal-screen';
import { ItemDetailsModalSheet } from '@/screens/items/item-details-modal-sheet';
import { KeyboardScope } from '@/platform/shortcuts/operator-shortcuts';
import { KeyboardShortcutsProvider } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { KeyboardShortcutsSheet } from '@/components/keyboard-shortcuts/keyboard-shortcuts-sheet';
import { LocationManagerStack } from './location-manager-stack';
import { NavigationStateRoute } from '@/utils/navigation';
import {
  NetworkStatus,
  useNetworkStatus,
} from '@/contexts/network-status-context';
import { NewOrderActionsContextProvider } from '@/contexts/new-order-actions-context';
import { NewOrderContextProvider } from '@/contexts/new-order-context';
import { NewOrderQuantityStateContextProvider } from '@/contexts/new-order-quantity-state-context';
import { OfflineDownloadManagementContextProvider } from '@/contexts/offline-download-management-context';
import { OperatorIntercomProvider } from '@/contexts/intercom-context/operator-intercom-provider';
import { OrderGuideSettingsContextProvider } from '@/contexts/order-guide-settings-context';
import { OrderGuideStoreProvider } from '@/stores/order-guide-store';
import { PartialPaymentScreen } from '@/screens/payments/partial-payment-screen';
import { PaymentsContextProvider } from '@/contexts/payments-context';
import { Platform } from 'react-native';
import { QueuedOfflineOrdersStack } from './queued-offline-orders-stack';
import { RestaurantInviteEmployeeScreen } from '@/screens/restaurant/restaurant-invite-employee-screen';
import { TabCardApplicationScreen } from '@/screens/payments/tab-card-application-screen';
import { ThreadOrderEventsProvider } from '@/screens/order-inbounds/thread-order-events-context';
import { UpdateEmployeeScreen } from '@/screens/update-employee-screen';
import { useAuth } from '@/contexts/auth-context';
import { useNetworkErrorToast } from '@/hooks/use-network-error-toast';
import { useNetworkGracePeriod } from '@/hooks/use-network-grace-period';
import { useReplayOfflineOperations } from '@/hooks/offline-mode/use-replay-offline-operations';
import { useSaveNotificationToken } from '@/hooks/use-save-notification-token';
import {
  useSelectedFulfillment,
  useSelectedFulfillmentWithTimeslotCutoffs,
} from '@/hooks/use-selected-fulfillment';
import { useSetupApp } from '@/hooks/use-setup-app';
import { useStorefrontBootIntent } from '@/hooks/use-storefront-boot-intent';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useUserContext } from '@/contexts/user-context';
import { useVitally } from '@/hooks/use-vitally';
import { VariantPackInfoContextProvider } from '@/contexts/variant-pack-info-context';
import { ViewPADAgreementScreen } from '@/screens/payments/view-pad-agreement-screen';

const Stack = createStackNavigator<AppModalStackParams>();

export const GRACE_PERIOD = 2000;

// Keyboard shortcut scopes for operator portal.
// '*' wildcard matches all scopes - needed because operator portal uses many scopes
// (ORDER_SUMMARY, ACTION_SHEET, etc.) that would otherwise need to be listed explicitly.
// LOCATION_PICKER is enabled dynamically when the picker opens.
const operatorPortalKeyboardScopes = [
  KeyboardScope.GLOBAL,
  KeyboardScope.SCOPED,
  '*',
];

export const AppStack: React.FC = () => {
  const modalScreenOptions = useModalStackScreenOptions();
  const authStatus = useAuth((s) => s.authStatus);
  const previousAuthStatus = useAuth((s) => s.previousAuthStatus);
  const { selectedRestaurantUUID } = useUserContext();
  const { checkAndSaveToken } = useSaveNotificationToken();
  const timeslotCutoffFulfillmentHydrationEnabled =
    useSupplierDynamicConfigValue(
      'timeslot_cutoff_fulfillment_hydration_enabled',
    );

  useNetworkErrorToast();
  useSetupApp();
  useVitally();

  /*
   * AppTabsContainerStack remounts when the selected restaurant changes.
   * This means changing restaurants (manually or through selecting a notification)
   * will push the user back to the Order Guide screen.
   * Preserve last route by using a ref to prevent rerenders while ensuring
   * initialRouteName is set in AppTabs for the first render after remounting.
   */
  const lastActiveTabRouteRef = useRef<NavigationStateRoute>(null);

  /*
   * Maintain the notification sheet state above the provider to prevent
   * showing the sheet again when switching chats.
   */
  const [canShowNotificationSheet, setCanShowNotificationSheet] = useState(
    previousAuthStatus === AuthStatus.NotAuthenticated && Platform.OS !== 'web',
  );

  /*
   * Expo doesn't clear the latest notification response, causing it to re-run
   * the notification every time the notification listener mounts. If the app
   * was opened from a notification with a chat_uuid, the last notification will
   * be re-processed and will switch the restaurant back.
   * https://github.com/expo/expo/issues/18712#issue-1344205291
   */
  const [lastProcessedNotificationID, setLastProcessedNotificationID] =
    useState<string | null>(null);

  /*
   * One-shot latch for a /storefront/:productId boot URL (web deep link).
   * Held here, ABOVE the selectedRestaurantUUID key boundary below, for the
   * same reason as lastProcessedNotificationID: state inside the keyed
   * subtree dies on every restaurant switch, and a seed effect inside it
   * would re-apply on every remount and revert manual switches. The
   * navigation itself happens inside AppTabsContainerStack (post-remount,
   * with live navigators) via useStorefrontBootNavigation.
   */
  const { clearPendingStorefrontNavigation, pendingStorefrontNavigation } =
    useStorefrontBootIntent();

  const { networkStatus } = useNetworkStatus();
  const { setGraceTimeout, clearGraceTimeout } = useNetworkGracePeriod();
  const { replayOfflineOperations } = useReplayOfflineOperations();
  const hasReplayedThisConnection = useRef(false);

  useEffect(() => {
    // When the user goes online, replay offline operations if we
    // haven't already done so. If they go offline, cancel that
    // (handling any flapping state of their network).
    if (networkStatus === NetworkStatus.CONNECTED) {
      setGraceTimeout(async () => {
        if (!hasReplayedThisConnection.current) {
          hasReplayedThisConnection.current = true;
          await replayOfflineOperations();
        }
      });
    }

    if (networkStatus === NetworkStatus.DISCONNECTED) {
      hasReplayedThisConnection.current = false;
      clearGraceTimeout();
    }

    return () => {
      clearGraceTimeout();
    };
  }, [
    clearGraceTimeout,
    networkStatus,
    replayOfflineOperations,
    setGraceTimeout,
  ]);

  // save notification token when user *first* authenticates
  // AppStack only renders for authenticated users with chat access,
  // so this guarantees the token save only happens for users who pass
  // all permission checks (including having chats)
  useEffect(() => {
    if (
      authStatus === AuthStatus.Authenticated &&
      previousAuthStatus === AuthStatus.NotAuthenticated
    ) {
      checkAndSaveToken().catch((error) => {
        // silently fail - notification tokens are not critical
        console.warn('failed to save notification token:', error);
      });
    }
  }, [authStatus, previousAuthStatus, checkAndSaveToken]);

  return (
    <BluetoothContextProvider>
      <OrderGuideSettingsContextProvider>
        <ActionSheetManagerContextProvider>
          <ChatsContextProvider>
            <OfflineDownloadManagementContextProvider>
              <React.Fragment key={selectedRestaurantUUID}>
                <ImpressionContextProvider>
                  <OperatorIntercomProvider>
                    <PaymentsContextProvider>
                      <NewOrderQuantityStateContextProvider>
                        <OrderGuideStoreProvider>
                          <NewOrderContextProvider multiCartSupported>
                            <NewOrderActionsContextProvider
                              useFulfillmentHook={
                                timeslotCutoffFulfillmentHydrationEnabled
                                  ? useSelectedFulfillmentWithTimeslotCutoffs
                                  : useSelectedFulfillment
                              }
                            >
                              <VariantPackInfoContextProvider>
                                <KeyboardShortcutsProvider
                                  scopes={operatorPortalKeyboardScopes}
                                >
                                  <ThreadOrderEventsProvider>
                                    {Platform.OS === 'web' ? (
                                      <KeyboardShortcutsSheet />
                                    ) : null}
                                    <Stack.Navigator
                                      initialRouteName={APP_MODAL_ROUTES.Base}
                                      screenOptions={modalScreenOptions}
                                    >
                                      <Stack.Screen
                                        children={() => (
                                          <AppTabsContainerStack
                                            canShowNotificationSheet={
                                              canShowNotificationSheet
                                            }
                                            clearPendingStorefrontNavigation={
                                              clearPendingStorefrontNavigation
                                            }
                                            lastActiveTabRouteRef={
                                              lastActiveTabRouteRef
                                            }
                                            lastProcessedNotificationID={
                                              lastProcessedNotificationID
                                            }
                                            onActionSheetShown={() =>
                                              setCanShowNotificationSheet(false)
                                            }
                                            pendingStorefrontNavigation={
                                              pendingStorefrontNavigation
                                            }
                                            setLastProcessedNotificationID={
                                              setLastProcessedNotificationID
                                            }
                                          />
                                        )}
                                        name={APP_MODAL_ROUTES.Base}
                                      />
                                      <Stack.Screen
                                        component={ContactSelectorScreen}
                                        name={APP_MODAL_ROUTES.ContactSelector}
                                      />
                                      <Stack.Screen
                                        component={CreateSubscriptionScreen}
                                        name={
                                          APP_MODAL_ROUTES.CreateSubscription
                                        }
                                      />
                                      <Stack.Screen
                                        component={EditNoteScreen}
                                        name={APP_MODAL_ROUTES.EditNote}
                                      />
                                      <Stack.Screen
                                        component={EditOrderItemNoteModalScreen}
                                        name={
                                          APP_MODAL_ROUTES.EditOrderItemNoteModal
                                        }
                                      />
                                      <Stack.Screen
                                        component={EditSubscriptionScreen}
                                        name={APP_MODAL_ROUTES.EditSubscription}
                                      />
                                      <Stack.Screen
                                        component={CreditRequestStack}
                                        name={APP_MODAL_ROUTES.CreditRequest}
                                        options={{
                                          cardStyle: flexModalCardStyle,
                                        }}
                                      />
                                      <Stack.Screen
                                        component={LocationManagerStack}
                                        name={APP_MODAL_ROUTES.LocationManager}
                                        options={{
                                          cardStyle: flexModalCardStyle,
                                        }}
                                      />
                                      <Stack.Screen
                                        component={
                                          RestaurantInviteEmployeeScreen
                                        }
                                        name={
                                          APP_MODAL_ROUTES.RestaurantInviteEmployee
                                        }
                                      />
                                      <Stack.Screen
                                        component={UpdateEmployeeScreen}
                                        name={APP_MODAL_ROUTES.UpdateEmployee}
                                      />
                                      <Stack.Screen
                                        component={
                                          AccountNotificationsModalScreen
                                        }
                                        name={
                                          APP_MODAL_ROUTES.AccountNotificationSettings
                                        }
                                      />
                                      <Stack.Screen
                                        component={AddPADScreen}
                                        name={APP_MODAL_ROUTES.AddPAD}
                                      />
                                      <Stack.Screen
                                        component={AddPaymentMethodScreen}
                                        name={APP_MODAL_ROUTES.AddPaymentMethod}
                                      />
                                      <Stack.Screen
                                        component={CheckCaptureScreen}
                                        name={APP_MODAL_ROUTES.CheckCapture}
                                      />
                                      <Stack.Screen
                                        component={CollectPADConsentScreen}
                                        name={
                                          APP_MODAL_ROUTES.CollectPADConsent
                                        }
                                      />
                                      <Stack.Screen
                                        component={PartialPaymentScreen}
                                        name={APP_MODAL_ROUTES.PartialPayment}
                                      />
                                      <Stack.Screen
                                        component={QueuedOfflineOrdersStack}
                                        name={
                                          APP_MODAL_ROUTES.QueuedOfflineOrders
                                        }
                                        options={{
                                          cardStyle: flexModalCardStyle,
                                        }}
                                      />
                                      <Stack.Screen
                                        component={DSRToolingStack}
                                        name={APP_MODAL_ROUTES.DSRTooling}
                                        options={{
                                          cardStyle: flexModalCardStyle,
                                        }}
                                      />
                                      <Stack.Screen
                                        component={HiddenAlertsStack}
                                        name={APP_MODAL_ROUTES.HiddenAlerts}
                                        options={{
                                          cardStyle: flexModalCardStyle,
                                        }}
                                      />
                                      <Stack.Screen
                                        component={InvoiceDetailModalScreen}
                                        name={
                                          APP_MODAL_ROUTES.InvoiceDetailModal
                                        }
                                        options={{
                                          cardStyle: flexModalCardStyle,
                                        }}
                                      />
                                      <Stack.Screen
                                        component={ItemDetailsModalSheet}
                                        name={APP_MODAL_ROUTES.ItemDetails}
                                        options={{
                                          cardStyle: flexModalCardStyle,
                                        }}
                                      />
                                      <Stack.Screen
                                        component={TabCardApplicationScreen}
                                        name={
                                          APP_MODAL_ROUTES.TabCardApplication
                                        }
                                      />
                                      <Stack.Screen
                                        component={ViewPADAgreementScreen}
                                        name={APP_MODAL_ROUTES.ViewPADAgreement}
                                      />
                                    </Stack.Navigator>
                                    <AppOverlays />
                                  </ThreadOrderEventsProvider>
                                </KeyboardShortcutsProvider>
                              </VariantPackInfoContextProvider>
                            </NewOrderActionsContextProvider>
                          </NewOrderContextProvider>
                        </OrderGuideStoreProvider>
                      </NewOrderQuantityStateContextProvider>
                    </PaymentsContextProvider>
                  </OperatorIntercomProvider>
                </ImpressionContextProvider>
              </React.Fragment>
            </OfflineDownloadManagementContextProvider>
          </ChatsContextProvider>
        </ActionSheetManagerContextProvider>
      </OrderGuideSettingsContextProvider>
    </BluetoothContextProvider>
  );
};
